import { useCallback, useEffect, useMemo, useState } from 'react';

import AgentsStep from '../components/AgentsStep';
import CodebaseStep from '../components/CodebaseStep';
import FooterBar from '../components/FooterBar';
import GitHubProfileMenu from '../components/GitHubProfileMenu';
import { ResultEmailPromptModal } from '../components/PricingStep';
import ScanSummary from '../components/ScanSummary';
import {
  WebPentestReviewStep,
  WebPentestScopeStep,
  type WebPentestCoverageOption
} from '../components/WebPentestSteps';
import WorkflowShell from '../components/WorkflowShell';
import {
  INITIAL_STEP,
  clearGitHubCallbackParams,
  compactScopePaths,
  getAiModelKey,
  getDiscoveryAgentModels,
  getGitHubCallbackError,
  getGitHubSetupInstallationId,
  getInitialGitHubSessionId,
  getNextWorkflowStep,
  getOrderedSelectedAiModels,
  getPreviousWorkflowStep,
  getSelectedAiAgentsWithModels,
  isRecord,
  isStepId,
  normalizeList,
  normalizeStoredText,
  requestJson,
  storeGitHubInstallationId,
  storeGitHubSessionId,
  type AgentDiscoveryResponse,
  type AgentModelsByAgent,
  type GitHubAppConfigResponse,
  type GitHubBranchesResponse,
  type GitHubInstallationSummary,
  type GitHubInstallationsResponse,
  type GitHubRepositoriesByInstallation,
  type GitHubRepositoriesResponse,
  type GitHubRepositoryBranchSummary,
  type GitHubRepositoryChoice,
  type GitHubRepositoryDirectorySummary,
  type GitHubRepositoryTreeResponse,
  type GitHubSessionResponse,
  type LaunchState,
  type LoadState,
  type PricingTier,
  type ScanAcceptedResponse,
  type StepId
} from '../components/workflowShared';

interface WebPentestPageProps {
  onNavigate: () => void;
}

interface WebPentestDraft {
  selectedScanType: 'web';
  currentStep: StepId;
  selectedAiModels: string[];
  targetUrl: string;
  authContext: string;
  testAccountContext: string;
  scopeNotes: string;
  selectedCoverage: string[];
  selectedGithubRepositoryId: number | null;
  selectedGithubBranch: string;
  scopePath: string;
  scopePaths: string[];
}

const WEB_PENTEST_DRAFT_STORAGE_KEY = 'scas-web-pentest-draft';
const WEB_PENTEST_COVERAGE_OPTIONS: WebPentestCoverageOption[] = [
  { id: 'trail-of-bits-static-analysis', title: 'Trail of bits', category: 'Vulnerability Analysis' },
  { id: 'semgrep-code-security', title: 'Semgrep', category: 'Vulnerability Analysis' },
  { id: 'trail-of-bits-supply-chain', title: 'Trail of bits', category: 'Supply Chain' }
];
const WEB_PENTEST_COVERAGE_ALIAS_BY_VALUE = new Map(
  WEB_PENTEST_COVERAGE_OPTIONS.flatMap((option) => [
    [option.id.toLowerCase(), option.id],
    [option.title.toLowerCase(), option.id]
  ])
);

const WEB_PENTEST_STEP_LABELS: Record<StepId, string> = {
  agents: 'AI Agents',
  skills: 'Scope',
  codebase: 'Target',
  pricing: 'Review'
};

const getCoverageIds = (coverageOptions: WebPentestCoverageOption[]): string[] => {
  return coverageOptions.map((option) => option.id);
};

const normalizeWebPentestCoverageSelection = (
  value: unknown,
  coverageOptions: WebPentestCoverageOption[] = WEB_PENTEST_COVERAGE_OPTIONS
): string[] => {
  const availableCoverage = new Set(getCoverageIds(coverageOptions));

  return Array.from(new Set(normalizeList(value)
    .map((option) => {
      return WEB_PENTEST_COVERAGE_ALIAS_BY_VALUE.get(option.trim().toLowerCase()) ?? option;
    })
    .filter((option) => availableCoverage.has(option))));
};

const getStoredWebPentestDraft = (): WebPentestDraft | null => {
  const rawValue = window.localStorage.getItem(WEB_PENTEST_DRAFT_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const value = JSON.parse(rawValue) as unknown;

    if (!isRecord(value)) {
      window.localStorage.removeItem(WEB_PENTEST_DRAFT_STORAGE_KEY);
      return null;
    }

    const scopePath = normalizeStoredText(value.scopePath);

    return {
      selectedScanType: 'web',
      currentStep: isStepId(value.currentStep) ? value.currentStep : INITIAL_STEP,
      selectedAiModels: normalizeList(value.selectedAiModels),
      targetUrl: normalizeStoredText(value.targetUrl),
      authContext: normalizeStoredText(value.authContext),
      testAccountContext: normalizeStoredText(value.testAccountContext),
      scopeNotes: normalizeStoredText(value.scopeNotes),
      selectedCoverage: normalizeWebPentestCoverageSelection(value.selectedCoverage),
      selectedGithubRepositoryId: normalizeStoredPositiveInteger(value.selectedGithubRepositoryId),
      selectedGithubBranch: normalizeStoredText(value.selectedGithubBranch),
      scopePath,
      scopePaths: normalizeStoredScopePaths(value.scopePaths, scopePath)
    };
  } catch {
    window.localStorage.removeItem(WEB_PENTEST_DRAFT_STORAGE_KEY);
    return null;
  }
};

const storeWebPentestDraft = (draft: WebPentestDraft): void => {
  window.localStorage.setItem(WEB_PENTEST_DRAFT_STORAGE_KEY, JSON.stringify(draft));
};

const normalizeStoredPositiveInteger = (value: unknown): number | null => {
  const number = typeof value === 'number' ? value : Number(value);

  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

const normalizeStoredScopePaths = (value: unknown, fallbackScopePath: string): string[] => {
  const scopePaths = normalizeList(value);

  return scopePaths.length > 0
    ? scopePaths
    : fallbackScopePath ? [fallbackScopePath] : [];
};

const getNormalizedTargetUrl = (targetUrl: string): string => {
  const trimmedTargetUrl = targetUrl.trim();

  if (!trimmedTargetUrl) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmedTargetUrl)) {
    return trimmedTargetUrl;
  }

  return `https://${trimmedTargetUrl}`;
};

const isValidWebTargetUrl = (targetUrl: string): boolean => {
  const normalizedTargetUrl = getNormalizedTargetUrl(targetUrl);

  if (!normalizedTargetUrl) {
    return false;
  }

  try {
    const parsedUrl = new URL(normalizedTargetUrl);

    return (parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:') && Boolean(parsedUrl.hostname);
  } catch {
    return false;
  }
};

function WebPentestPage({ onNavigate }: WebPentestPageProps) {
  const initialWebPentestDraft = useMemo(() => getStoredWebPentestDraft(), []);
  const [currentStep, setCurrentStep] = useState<StepId>(() => initialWebPentestDraft?.currentStep ?? INITIAL_STEP);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState('');
  const [availableAiAgents, setAvailableAiAgents] = useState<string[]>([]);
  const [agentModelsByAgent, setAgentModelsByAgent] = useState<AgentModelsByAgent>({});
  const [selectedAiModels, setSelectedAiModels] = useState<Set<string>>(() => new Set());
  const [coverageOptions, setCoverageOptions] = useState<WebPentestCoverageOption[]>(
    () => WEB_PENTEST_COVERAGE_OPTIONS
  );
  const [selectedCoverage, setSelectedCoverage] = useState<string[]>(
    () => initialWebPentestDraft?.selectedCoverage ?? getCoverageIds(WEB_PENTEST_COVERAGE_OPTIONS)
  );
  const [targetUrl, setTargetUrl] = useState(() => initialWebPentestDraft?.targetUrl ?? '');
  const [authContext, setAuthContext] = useState(() => initialWebPentestDraft?.authContext ?? '');
  const [testAccountContext, setTestAccountContext] = useState(() => initialWebPentestDraft?.testAccountContext ?? '');
  const [scopeNotes, setScopeNotes] = useState(() => initialWebPentestDraft?.scopeNotes ?? '');
  const [githubConfigState, setGithubConfigState] = useState<LoadState>('idle');
  const [githubConfigError, setGithubConfigError] = useState('');
  const [githubConfig, setGithubConfig] = useState<GitHubAppConfigResponse | null>(null);
  const [githubSessionId] = useState(() => getInitialGitHubSessionId());
  const [githubLogin, setGithubLogin] = useState('');
  const [githubSessionState, setGithubSessionState] = useState<LoadState>('idle');
  const [githubSessionError, setGithubSessionError] = useState(getGitHubCallbackError());
  const [githubInstallations, setGithubInstallations] = useState<GitHubInstallationSummary[]>([]);
  const [selectedGithubInstallationId, setSelectedGithubInstallationId] = useState<number | null>(
    () => getGitHubSetupInstallationId()
  );
  const [githubRepositoriesState, setGithubRepositoriesState] = useState<LoadState>('idle');
  const [githubRepositoriesError, setGithubRepositoriesError] = useState('');
  const [githubRepositoriesByInstallation, setGithubRepositoriesByInstallation] =
    useState<GitHubRepositoriesByInstallation>({});
  const [selectedGithubRepositoryId, setSelectedGithubRepositoryId] = useState<number | null>(
    () => initialWebPentestDraft?.selectedGithubRepositoryId ?? null
  );
  const [githubBranchesState, setGithubBranchesState] = useState<LoadState>('idle');
  const [githubBranchesError, setGithubBranchesError] = useState('');
  const [githubBranches, setGithubBranches] = useState<GitHubRepositoryBranchSummary[]>([]);
  const [selectedGithubBranch, setSelectedGithubBranch] = useState(
    () => initialWebPentestDraft?.selectedGithubBranch ?? ''
  );
  const [githubTreeState, setGithubTreeState] = useState<LoadState>('idle');
  const [githubTreeError, setGithubTreeError] = useState('');
  const [githubTreeDirectories, setGithubTreeDirectories] = useState<GitHubRepositoryDirectorySummary[]>([]);
  const [scopePaths, setScopePaths] = useState<string[]>(() => initialWebPentestDraft?.scopePaths ?? []);
  const [launchState, setLaunchState] = useState<LaunchState>('idle');
  const [launchError, setLaunchError] = useState('');
  const [scanResponse, setScanResponse] = useState<ScanAcceptedResponse | null>(null);
  const [resultEmail, setResultEmail] = useState('');
  const [isResultEmailModalOpen, setIsResultEmailModalOpen] = useState(false);

  const orderedSelectedAiModels = getOrderedSelectedAiModels(availableAiAgents, agentModelsByAgent, selectedAiModels);
  const orderedSelectedCoverage = useMemo(() => {
    return coverageOptions
      .filter((option) => selectedCoverage.includes(option.id))
      .map((option) => option.id);
  }, [coverageOptions, selectedCoverage]);
  const selectedCoverageLabels = useMemo(() => {
    const selectedCoverageSet = new Set(orderedSelectedCoverage);

    return coverageOptions
      .filter((option) => selectedCoverageSet.has(option.id))
      .map((option) => option.title);
  }, [coverageOptions, orderedSelectedCoverage]);
  const selectedModelCount = orderedSelectedAiModels.length;
  const selectedPricingTier: PricingTier = 'free';
  const selectedPricingTierAvailable = true;
  const target = getNormalizedTargetUrl(targetUrl);
  const scopePath = scopePaths.join(', ');
  const selectedGithubInstallation = githubInstallations.find((installation) => {
    return installation.id === selectedGithubInstallationId;
  });
  const githubRepositoryChoices = useMemo<GitHubRepositoryChoice[]>(() => {
    return githubInstallations.flatMap((installation) => {
      const repositories = githubRepositoriesByInstallation[installation.id] ?? [];

      return repositories.map((repository) => ({
        installation,
        repository
      }));
    });
  }, [githubInstallations, githubRepositoriesByInstallation]);
  const selectedGithubRepository = (selectedGithubInstallationId
    ? githubRepositoriesByInstallation[selectedGithubInstallationId] ?? []
    : []
  ).find((repository) => {
    return repository.id === selectedGithubRepositoryId;
  });
  const repositoryTarget = selectedGithubRepository?.fullName ?? '';
  const hasAcceptedScan = launchState === 'accepted' && scanResponse !== null;
  const hasCodebaseTarget = Boolean(
    githubSessionId &&
    selectedGithubInstallationId &&
    selectedGithubRepository &&
    githubBranchesState === 'ready' &&
    selectedGithubBranch
  );
  const showTargetUrlError = Boolean(targetUrl.trim()) && !isValidWebTargetUrl(targetUrl);
  const canAdvance =
    loadState === 'ready' && (
      (currentStep === 'agents' && selectedModelCount > 0) ||
      (currentStep === 'skills' && orderedSelectedCoverage.length > 0) ||
      (currentStep === 'codebase' && isValidWebTargetUrl(targetUrl) && hasCodebaseTarget) ||
      (currentStep === 'pricing' && launchState !== 'submitting')
    );

  const buildWebPentestDraft = useCallback((step: StepId): WebPentestDraft => ({
    selectedScanType: 'web',
    currentStep: step,
    selectedAiModels: Array.from(selectedAiModels),
    targetUrl,
    authContext,
    testAccountContext,
    scopeNotes,
    selectedCoverage: orderedSelectedCoverage,
    selectedGithubRepositoryId,
    selectedGithubBranch,
    scopePath,
    scopePaths
  }), [
    selectedAiModels,
    targetUrl,
    authContext,
    testAccountContext,
    scopeNotes,
    orderedSelectedCoverage,
    selectedGithubRepositoryId,
    selectedGithubBranch,
    scopePath,
    scopePaths
  ]);

  const viewRun = useCallback((runId: string) => {
    storeWebPentestDraft(buildWebPentestDraft(INITIAL_STEP));
    setCurrentStep(INITIAL_STEP);
    setLaunchState('idle');
    setScanResponse(null);
    setLaunchError('');

    const nextPath = `/run/${encodeURIComponent(runId)}`;

    window.history.pushState({}, document.title, nextPath);
    onNavigate();
  }, [buildWebPentestDraft, onNavigate]);

  const resetLaunchState = useCallback(() => {
    setLaunchState('idle');
    setLaunchError('');
    setScanResponse(null);
  }, []);

  const backToScanTypeSelection = useCallback(() => {
    storeWebPentestDraft(buildWebPentestDraft(INITIAL_STEP));
    setCurrentStep(INITIAL_STEP);
    resetLaunchState();

    if (window.location.pathname !== '/') {
      window.history.pushState({}, document.title, '/');
      onNavigate();
    }
  }, [buildWebPentestDraft, onNavigate, resetLaunchState]);

  const loadOptions = useCallback(async () => {
    setLoadState('loading');
    setLoadError('');

    try {
      const discovery = await requestJson<AgentDiscoveryResponse>('/agents');
      const aiAgents = normalizeList(discovery.aiAgents);
      const aiAgentModels = getDiscoveryAgentModels(discovery, aiAgents);
      const discoveredWebApiSkills = normalizeList(discovery.webApiSkills);
      const nextCoverageOptions = discoveredWebApiSkills.length > 0
        ? WEB_PENTEST_COVERAGE_OPTIONS.filter((option) => discoveredWebApiSkills.includes(option.id))
        : WEB_PENTEST_COVERAGE_OPTIONS;
      const storedDraft = getStoredWebPentestDraft();
      const validAiModelKeys = new Set(aiAgents.flatMap((agent) => {
        return (aiAgentModels[agent] ?? []).map((model) => getAiModelKey(agent, model));
      }));

      setAvailableAiAgents(aiAgents);
      setAgentModelsByAgent(aiAgentModels);
      setCoverageOptions(nextCoverageOptions);
      setSelectedCoverage(
        storedDraft
          ? normalizeWebPentestCoverageSelection(storedDraft.selectedCoverage, nextCoverageOptions)
          : getCoverageIds(nextCoverageOptions)
      );
      setSelectedAiModels(
        storedDraft
          ? new Set(storedDraft.selectedAiModels.filter((modelKey) => validAiModelKeys.has(modelKey)))
          : new Set()
      );
      setLoadState('ready');
    } catch (error) {
      setAvailableAiAgents([]);
      setAgentModelsByAgent({});
      setCoverageOptions(WEB_PENTEST_COVERAGE_OPTIONS);
      setSelectedAiModels(new Set());
      setLoadError(error instanceof Error ? error.message : String(error));
      setLoadState('error');
    }
  }, []);

  const loadGitHubConfig = useCallback(async () => {
    setGithubConfigState('loading');
    setGithubConfigError('');

    try {
      const config = await requestJson<GitHubAppConfigResponse>('/github/app');

      setGithubConfig(config);
      setGithubConfigState('ready');
    } catch (error) {
      setGithubConfig(null);
      setGithubConfigError(error instanceof Error ? error.message : String(error));
      setGithubConfigState('error');
    }
  }, []);

  const loadGitHubSession = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      setGithubLogin('');
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setGithubSessionState('idle');
      return;
    }

    setGithubSessionState('loading');
    setGithubSessionError('');

    try {
      const [session, installationResponse] = await Promise.all([
        requestJson<GitHubSessionResponse>(`/github/session/${encodeURIComponent(sessionId)}`),
        requestJson<GitHubInstallationsResponse>(`/github/session/${encodeURIComponent(sessionId)}/installations`)
      ]);
      const installations = Array.isArray(installationResponse.installations)
        ? installationResponse.installations
        : [];

      setGithubLogin(session.login ?? '');
      setGithubInstallations(installations);
      setGithubSessionState('ready');

      setSelectedGithubInstallationId((currentInstallationId) => {
        if (currentInstallationId && installations.some((installation) => installation.id === currentInstallationId)) {
          return currentInstallationId;
        }

        return installations[0]?.id ?? null;
      });
    } catch (error) {
      setGithubLogin('');
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setGithubSessionState('error');
      setGithubSessionError(error instanceof Error ? error.message : String(error));
      storeGitHubSessionId('');
    }
  }, []);

  const loadGitHubRepositories = useCallback(async (
    sessionId: string,
    installations: GitHubInstallationSummary[]
  ) => {
    if (!sessionId || installations.length === 0) {
      setGithubRepositoriesByInstallation({});
      setGithubRepositoriesState(sessionId ? 'ready' : 'idle');
      setGithubRepositoriesError('');
      return;
    }

    setGithubRepositoriesState('loading');
    setGithubRepositoriesError('');

    try {
      const entries = await Promise.all(installations.map(async (installation) => {
        const response = await requestJson<GitHubRepositoriesResponse>(
          `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installation.id)}/repositories`
        );
        const repositories = Array.isArray(response.repositories) ? response.repositories : [];

        return [installation.id, repositories] as const;
      }));
      const repositoriesByInstallation = entries.reduce<GitHubRepositoriesByInstallation>(
        (groupedRepositories, [installationId, repositories]) => ({
          ...groupedRepositories,
          [installationId]: repositories
        }),
        {}
      );

      setGithubRepositoriesByInstallation(repositoriesByInstallation);
      setGithubRepositoriesState('ready');
    } catch (error) {
      setGithubRepositoriesByInstallation({});
      setSelectedGithubRepositoryId(null);
      setGithubRepositoriesState('error');
      setGithubRepositoriesError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const loadGitHubBranches = useCallback(async (
    sessionId: string,
    installationId: number,
    repositoryId: number
  ) => {
    if (!sessionId || !installationId || !repositoryId) {
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      return;
    }

    setGithubBranchesState('loading');
    setGithubBranchesError('');

    try {
      const response = await requestJson<GitHubBranchesResponse>(
        `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installationId)}/repositories/${String(repositoryId)}/branches`
      );
      const branches = Array.isArray(response.branches) ? response.branches : [];
      const fallbackBranch = branches.find((branch) => branch.default)?.name ?? branches[0]?.name ?? '';

      setGithubBranches(branches);
      setSelectedGithubBranch((currentBranch) => {
        return branches.some((branch) => branch.name === currentBranch)
          ? currentBranch
          : fallbackBranch;
      });
      setGithubBranchesState('ready');
    } catch (error) {
      setGithubBranches([]);
      setSelectedGithubBranch('');
      setGithubBranchesState('error');
      setGithubBranchesError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const loadGitHubRepositoryTree = useCallback(async (
    sessionId: string,
    installationId: number,
    repositoryId: number,
    branch: string,
    signal: AbortSignal
  ) => {
    if (!sessionId || !installationId || !repositoryId || !branch) {
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      return;
    }

    setGithubTreeState('loading');
    setGithubTreeError('');

    try {
      const response = await requestJson<GitHubRepositoryTreeResponse>(
        `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installationId)}/repositories/${String(repositoryId)}/tree?branch=${encodeURIComponent(branch)}`,
        { signal }
      );
      const directories = Array.isArray(response.directories) ? response.directories : [];

      setGithubTreeDirectories(directories);
      setScopePaths((currentScopePaths) => {
        const availablePaths = new Set(directories.map((directory) => directory.path));

        return compactScopePaths(currentScopePaths.filter((path) => availablePaths.has(path)));
      });
      setGithubTreeState('ready');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      setGithubTreeDirectories([]);
      setGithubTreeState('error');
      setGithubTreeError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    void loadGitHubConfig();
  }, [loadGitHubConfig]);

  useEffect(() => {
    if (loadState !== 'ready') {
      return;
    }

    storeWebPentestDraft(buildWebPentestDraft(currentStep));
  }, [
    loadState,
    currentStep,
    buildWebPentestDraft
  ]);

  useEffect(() => {
    if (githubSessionId) {
      storeGitHubSessionId(githubSessionId);
      void loadGitHubSession(githubSessionId);
    } else {
      setGithubLogin('');
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setSelectedGithubInstallationId(null);
      setSelectedGithubRepositoryId(null);
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
    }

    clearGitHubCallbackParams();
  }, [githubSessionId, loadGitHubSession]);

  useEffect(() => {
    if (!githubSessionId) {
      setGithubRepositoriesByInstallation({});
      setSelectedGithubRepositoryId(null);
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubRepositoriesState('idle');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
      return;
    }

    if (githubSessionState === 'ready') {
      void loadGitHubRepositories(githubSessionId, githubInstallations);
    } else {
      setGithubRepositoriesByInstallation({});
      setGithubRepositoriesState(githubSessionState === 'loading' ? 'loading' : 'idle');
    }
  }, [githubSessionId, githubSessionState, githubInstallations, loadGitHubRepositories]);

  useEffect(() => {
    if (!githubSessionId || githubRepositoriesState !== 'ready') {
      return;
    }

    if (githubRepositoryChoices.length === 0) {
      setSelectedGithubRepositoryId(null);
      setSelectedGithubBranch('');
      setScopePaths([]);
      return;
    }

    const selectedChoice = githubRepositoryChoices.find((choice) => {
      return choice.installation.id === selectedGithubInstallationId &&
        choice.repository.id === selectedGithubRepositoryId;
    });

    if (selectedChoice) {
      return;
    }

    const matchingRepositoryChoice = selectedGithubRepositoryId
      ? githubRepositoryChoices.find((choice) => choice.repository.id === selectedGithubRepositoryId)
      : null;
    const nextChoice = matchingRepositoryChoice ?? (
      githubRepositoryChoices.length === 1 ? githubRepositoryChoices[0] : null
    );

    if (nextChoice) {
      setSelectedGithubInstallationId(nextChoice.installation.id);
      setSelectedGithubRepositoryId(nextChoice.repository.id);
      return;
    }

    setSelectedGithubRepositoryId(null);
    setSelectedGithubBranch('');
    setScopePaths([]);
  }, [
    githubRepositoryChoices,
    githubRepositoriesState,
    githubSessionId,
    selectedGithubInstallationId,
    selectedGithubRepositoryId
  ]);

  useEffect(() => {
    storeGitHubInstallationId(selectedGithubInstallationId);
  }, [selectedGithubInstallationId]);

  useEffect(() => {
    if (!githubSessionId || !selectedGithubInstallationId || !selectedGithubRepository) {
      setGithubBranches([]);
      setGithubBranchesState(githubSessionId && selectedGithubRepositoryId ? 'loading' : 'idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
      return;
    }

    void loadGitHubBranches(githubSessionId, selectedGithubInstallationId, selectedGithubRepository.id);
  }, [
    githubSessionId,
    selectedGithubInstallationId,
    selectedGithubRepository,
    selectedGithubRepository?.id,
    selectedGithubRepositoryId,
    loadGitHubBranches
  ]);

  useEffect(() => {
    if (!hasCodebaseTarget || !githubSessionId || !selectedGithubInstallationId || !selectedGithubRepository) {
      setGithubTreeDirectories([]);
      setGithubTreeState(hasCodebaseTarget ? 'loading' : 'idle');
      setGithubTreeError('');
      return;
    }

    const abortController = new AbortController();

    void loadGitHubRepositoryTree(
      githubSessionId,
      selectedGithubInstallationId,
      selectedGithubRepository.id,
      selectedGithubBranch,
      abortController.signal
    );

    return () => abortController.abort();
  }, [
    githubSessionId,
    hasCodebaseTarget,
    selectedGithubBranch,
    selectedGithubInstallationId,
    selectedGithubRepository,
    selectedGithubRepository?.id,
    loadGitHubRepositoryTree
  ]);

  const moveNext = () => {
    setCurrentStep(getNextWorkflowStep(currentStep));
  };

  const moveBack = () => {
    setCurrentStep(getPreviousWorkflowStep(currentStep));
  };

  const connectGitHub = () => {
    const connectUrl = githubConfig?.connectUrl;

    if (!connectUrl) {
      setGithubSessionError('GitHub App OAuth is not configured');
      return;
    }

    const returnTo = `${window.location.pathname}${window.location.search}`;

    window.location.href = `${connectUrl}?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const installGitHubApp = () => {
    const installUrl = githubConfig?.installUrl ?? selectedGithubInstallation?.htmlUrl;

    if (!installUrl) {
      setGithubSessionError('GitHub App install URL is not configured');
      return;
    }

    window.location.href = installUrl;
  };

  const refreshGitHubAccess = () => {
    void loadGitHubConfig();

    if (githubSessionId) {
      void loadGitHubSession(githubSessionId);
      void loadGitHubRepositories(githubSessionId, githubInstallations);

      if (selectedGithubInstallationId && selectedGithubRepository) {
        void loadGitHubBranches(githubSessionId, selectedGithubInstallationId, selectedGithubRepository.id);
      }
    }
  };

  const selectGithubRepository = (installationId: number, repositoryId: number) => {
    const repository = githubRepositoryChoices.find((choice) => {
      return choice.installation.id === installationId && choice.repository.id === repositoryId;
    })?.repository;

    setSelectedGithubInstallationId(installationId);
    setSelectedGithubRepositoryId(repositoryId);
    setSelectedGithubBranch(repository?.defaultBranch ?? '');
    setGithubBranches([]);
    setGithubBranchesState('loading');
    setGithubBranchesError('');
    setGithubTreeDirectories([]);
    setGithubTreeState('loading');
    setGithubTreeError('');
    setScopePaths([]);
    resetLaunchState();
  };

  const selectGithubBranch = (branch: string) => {
    setSelectedGithubBranch(branch);
    setScopePaths([]);
    setGithubTreeDirectories([]);
    setGithubTreeState('loading');
    setGithubTreeError('');
    resetLaunchState();
  };

  const selectRepositoryRootScope = () => {
    setScopePaths([]);
    resetLaunchState();
  };

  const toggleScopePath = (scopePathToToggle: string) => {
    setScopePaths((currentScopePaths) => {
      if (currentScopePaths.includes(scopePathToToggle)) {
        return currentScopePaths.filter((path) => path !== scopePathToToggle);
      }

      const withoutCoveringAncestors = currentScopePaths.filter((path) => {
        return !scopePathToToggle.startsWith(`${path}/`);
      });

      return compactScopePaths([...withoutCoveringAncestors, scopePathToToggle]);
    });
    resetLaunchState();
  };

  const toggleModelSelection = (agent: string, model: string) => {
    const modelKey = getAiModelKey(agent, model);

    setSelectedAiModels((previous) => {
      const next = new Set(previous);

      if (next.has(modelKey)) {
        next.delete(modelKey);
      } else {
        next.add(modelKey);
      }

      return next;
    });
    resetLaunchState();
  };

  const toggleCoverage = (coverage: string) => {
    setSelectedCoverage((previousCoverage) => {
      if (previousCoverage.includes(coverage)) {
        return previousCoverage.filter((item) => item !== coverage);
      }

      return getCoverageIds(coverageOptions).filter((option) => {
        return option === coverage || previousCoverage.includes(option);
      });
    });
    resetLaunchState();
  };

  const launchScan = async (email: string) => {
    if (currentStep !== 'pricing' || !canAdvance) {
      return;
    }

    if (!githubSessionId || !selectedGithubInstallationId || !selectedGithubRepository || !selectedGithubBranch) {
      setLaunchError('Select a GitHub repository and branch before launching the pentest.');
      setLaunchState('error');
      return;
    }

    setLaunchState('submitting');
    setLaunchError('');
    setScanResponse(null);

    try {
      const agentsWithModels = getSelectedAiAgentsWithModels(orderedSelectedAiModels);
      const github = {
        app: {
          sessionId: githubSessionId,
          installationId: selectedGithubInstallationId,
          repositoryId: selectedGithubRepository.id,
          owner: selectedGithubRepository.owner,
          repo: selectedGithubRepository.repo,
          branch: selectedGithubBranch
        }
      };

      const response = await requestJson<ScanAcceptedResponse>('/scan', {
        method: 'POST',
        body: JSON.stringify({
          scanType: 'web-api',
          github,
          targetUrl: target,
          web: {
            targetUrl: target,
            authContext: authContext.trim() || undefined,
            testAccountContext: testAccountContext.trim() || undefined,
            scopeNotes: scopeNotes.trim() || undefined
          },
          scopePaths,
          skills: orderedSelectedCoverage,
          aiAgents: agentsWithModels,
          pricingTier: 'free',
          resultEmail: email
        })
      });

      setScanResponse(response);
      setLaunchState('accepted');
      storeWebPentestDraft(buildWebPentestDraft(INITIAL_STEP));
    } catch (error) {
      setLaunchError(error instanceof Error ? error.message : String(error));
      setLaunchState('error');
    }
  };

  const openResultEmailPrompt = () => {
    if (currentStep === 'pricing' && canAdvance) {
      setIsResultEmailModalOpen(true);
    }
  };

  const submitResultEmail = (email: string) => {
    setResultEmail(email);
    setIsResultEmailModalOpen(false);
    void launchScan(email);
  };

  const overlay = (
    <ResultEmailPromptModal
      isOpen={isResultEmailModalOpen}
      initialEmail={resultEmail}
      launchState={launchState}
      onCancel={() => setIsResultEmailModalOpen(false)}
      onSubmit={submitResultEmail}
    />
  );

  const headerActions = (
    <GitHubProfileMenu
      githubSessionId={githubSessionId}
      githubLogin={githubLogin}
      githubConfigState={githubConfigState}
      githubSessionState={githubSessionState}
      githubRepositoriesState={githubRepositoriesState}
      onRefreshGitHubAccess={refreshGitHubAccess}
    />
  );
  const summary = (
    <ScanSummary
      orderedSelectedSkills={orderedSelectedCoverage}
      orderedSelectedAiModels={orderedSelectedAiModels}
      target={target}
      branch={selectedGithubBranch}
      scopePaths={scopePaths}
      selectionLabel="Coverage"
      targetLabel="Target URL"
      branchLabel="Source branch"
      scopeLabel="Source scope"
    />
  );
  const footer = !hasAcceptedScan ? (
    <FooterBar
      currentStep={currentStep}
      selectedSkillsCount={orderedSelectedCoverage.length}
      selectedModelCount={selectedModelCount}
      selectedPricingTier={selectedPricingTier}
      selectedPricingTierAvailable={selectedPricingTierAvailable}
      orderedSelectedSkillsCount={orderedSelectedCoverage.length}
      canAdvance={canAdvance}
      launchState={launchState}
      selectionLabelSingular="coverage area"
      selectionLabelPlural="coverage areas"
      pricingFooterCopy="Free scan ready."
      launchButtonLabel="Free scan"
      onBack={moveBack}
      onBackToSelection={backToScanTypeSelection}
      onNext={moveNext}
      onLaunchScan={openResultEmailPrompt}
    />
  ) : null;
  const webTargetAdditionalInfo = (
    <div className="codebase-questionnaire">
      <label className="field">
        <span>Target URL</span>
        <input
          type="url"
          value={targetUrl}
          placeholder="https://app.example.com"
          onChange={(event) => {
            setTargetUrl(event.target.value);
            resetLaunchState();
          }}
        />
      </label>
      {showTargetUrlError && (
        <p className="codebase-status error">Enter a valid HTTP or HTTPS target.</p>
      )}

      <label className="field">
        <span>Authentication context</span>
        <textarea
          className="codebase-textarea"
          value={authContext}
          placeholder="SSO, OAuth, magic links, role boundaries, session rules..."
          onChange={(event) => {
            setAuthContext(event.target.value);
            resetLaunchState();
          }}
          rows={3}
        />
      </label>

      <label className="field">
        <span>Test account context</span>
        <textarea
          className="codebase-textarea"
          value={testAccountContext}
          placeholder="Describe available test accounts, roles, and data boundaries..."
          onChange={(event) => {
            setTestAccountContext(event.target.value);
            resetLaunchState();
          }}
          rows={3}
        />
      </label>

      <label className="field">
        <span>Scope notes</span>
        <textarea
          className="codebase-textarea"
          value={scopeNotes}
          placeholder="Endpoints, environments, excluded paths, rate limits, or special constraints..."
          onChange={(event) => {
            setScopeNotes(event.target.value);
            resetLaunchState();
          }}
          rows={4}
        />
      </label>
    </div>
  );

  return (
    <WorkflowShell
      currentStep={currentStep}
      stepperLabel="Web pentest steps"
      stepLabels={WEB_PENTEST_STEP_LABELS}
      headerActions={headerActions}
      overlay={overlay}
      summary={summary}
      footer={footer}
    >
      {currentStep === 'agents' && (
        <AgentsStep
          loadState={loadState}
          loadError={loadError}
          availableAiAgents={availableAiAgents}
          agentModelsByAgent={agentModelsByAgent}
          selectedAiModels={selectedAiModels}
          onRetryLoadOptions={() => void loadOptions()}
          onToggleModel={toggleModelSelection}
        />
      )}

      {currentStep === 'skills' && (
        <WebPentestScopeStep
          coverageOptions={coverageOptions}
          selectedCoverage={selectedCoverage}
          onToggleCoverage={toggleCoverage}
        />
      )}

      {currentStep === 'codebase' && (
        <CodebaseStep
          headingTitle="Add your web target"
          headingDescription="Connect the source repository for this web or API assessment."
          additionalInfoButtonLabel="Add extra target information"
          additionalInfoTitle="Web target information"
          additionalInfoContent={webTargetAdditionalInfo}
          additionalInfoButtonPlacement="after-github-action"
          githubConnectLabel="Connect GitHub"
          githubAccessLabel="Access repository"
          githubConfigState={githubConfigState}
          githubConfigError={githubConfigError}
          githubConfig={githubConfig}
          githubSessionState={githubSessionState}
          githubSessionError={githubSessionError}
          githubSessionId={githubSessionId}
          githubInstallations={githubInstallations}
          selectedGithubInstallationId={selectedGithubInstallationId}
          githubRepositoriesState={githubRepositoriesState}
          githubRepositoriesError={githubRepositoriesError}
          githubRepositoryChoices={githubRepositoryChoices}
          selectedGithubRepositoryId={selectedGithubRepositoryId}
          githubBranchesState={githubBranchesState}
          githubBranchesError={githubBranchesError}
          githubBranches={githubBranches}
          selectedGithubBranch={selectedGithubBranch}
          githubTreeState={githubTreeState}
          githubTreeError={githubTreeError}
          githubTreeDirectories={githubTreeDirectories}
          scopePaths={scopePaths}
          nonStandardTokens=""
          trustedRoles=""
          regularFunctions=""
          adminRestrictions=""
          assumptionsFilePath=""
          externalContractsTrusted=""
          onConnectGitHub={connectGitHub}
          onInstallGitHubApp={installGitHubApp}
          onSelectGithubRepository={selectGithubRepository}
          onSelectGithubBranch={selectGithubBranch}
          onSelectRepositoryRootScope={selectRepositoryRootScope}
          onToggleScopePath={toggleScopePath}
          onNonStandardTokensChange={() => undefined}
          onTrustedRolesChange={() => undefined}
          onRegularFunctionsChange={() => undefined}
          onAdminRestrictionsChange={() => undefined}
          onAssumptionsFilePathChange={() => undefined}
          onExternalContractsTrustedChange={() => undefined}
        />
      )}

      {currentStep === 'pricing' && (
        <WebPentestReviewStep
          orderedSelectedAiModels={orderedSelectedAiModels}
          selectedCoverageLabels={selectedCoverageLabels}
          target={target}
          repository={repositoryTarget}
          branch={selectedGithubBranch}
          scopePaths={scopePaths}
          authContext={authContext}
          testAccountContext={testAccountContext}
          scopeNotes={scopeNotes}
          scanResponse={scanResponse}
          launchState={launchState}
          launchError={launchError}
          onViewRun={viewRun}
        />
      )}
    </WorkflowShell>
  );
}

export default WebPentestPage;
